import { useRef, useState } from 'react'

import { Box, Button, Card, Text } from '@factorialco/deprecated-design-system'
import { F0TagRaw } from '@factorialco/f0-react'
import { Alert, AlertDescription, AlertTitle } from '@factorialco/f0-react/dist/experimental'
import { Link, LinkRemove } from '@factorialco/f0-react/icons/app'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import i18n from 'modules/core/lib/i18n'

import RatesSingleForm from 'modules/project_management/components/ProfitabilityCards/RatesSingleForm'
import {
  calculateBillRateFromProfitability,
  calculateProfitabilityPercentage,
  convertCentsToPercentage,
} from 'modules/project_management/lib/profitabilityCalculations'

interface EditRateCardProps {
  areJobLevelRates?: boolean
  areProjectWorkerRates?: boolean
  billingRateCents: number
  currency: string
  referenceBillingRate?: number
  maxCostRate: number
  initialProfitMargin: number
  onClose?: () => void
  onSubmit: (values: { billRate: string; minProfitMargin: string }) => void
}

const EditRateCard = ({
  areJobLevelRates = false,
  areProjectWorkerRates = false,
  billingRateCents,
  currency,
  referenceBillingRate,
  maxCostRate,
  initialProfitMargin,
  onClose,
  onSubmit,
}: EditRateCardProps) => {
  const [errors, setErrors] = useState<{ billRate?: string; minProfitMargin?: string }>({})

  const calculateProfitMargin = (maxCostRateValue: number, billRateValue: string): string => {
    const bill = parseFloat(billRateValue) || 0
    return calculateProfitabilityPercentage(maxCostRateValue, bill)
  }

  const initialProfitMarginValue = convertCentsToPercentage(initialProfitMargin).toString()
  const [minProfitMargin, setMinProfitMargin] = useState<string>(initialProfitMarginValue)

  const lastUpdatedField = useRef<'profitMargin' | 'billRate' | null>(null)

  const calculateBillRate = (maxCostRateValue: number, profitMarginValue: string): string => {
    const parsedProfitMargin = parseFloat(profitMarginValue) || 0
    return calculateBillRateFromProfitability(maxCostRateValue, parsedProfitMargin)
  }

  const initialBillRateValue = billingRateCents
    ? billingRateCents.toString()
    : calculateBillRate(maxCostRate, initialProfitMarginValue)

  const [billRate, setBillRate] = useState<string>(initialBillRateValue)

  const currentBillRate = parseFloat(billRate) || 0
  const currentProfitMargin = parseFloat(minProfitMargin) || 0
  const initialBillRate = parseFloat(initialBillRateValue) || 0
  const initialProfit = parseFloat(initialProfitMarginValue) || 0

  const hasInitialValuesChanged =
    currentBillRate !== initialBillRate || currentProfitMargin !== initialProfit

  const handleBillRateChange = (value: string) => {
    lastUpdatedField.current = 'billRate'
    setBillRate(value)

    if (+value <= 0 || value === '') {
      setErrors({ ...errors, billRate: i18n.t('common.invalid_value') })
    } else {
      setErrors({ ...errors, billRate: undefined })
    }

    const newProfitMargin = calculateProfitMargin(maxCostRate, value)
    setMinProfitMargin(newProfitMargin)
  }

  const handleProfitMarginChange = (value: string) => {
    lastUpdatedField.current = 'profitMargin'
    setMinProfitMargin(value)

    const newBillRate = calculateBillRate(maxCostRate, value)
    setBillRate(newBillRate)
  }

  const handleSubmit = () => {
    onSubmit({ billRate, minProfitMargin })
    onClose?.()
  }

  const alertTitle = areJobLevelRates
    ? i18n.t('project_management.jobs_and_rates.sidepanels.alerts.not_affect_projects.title')
    : areProjectWorkerRates
    ? i18n.t('project_management.project_workers.details.alerts.rate_changed.title')
    : i18n.t('project_management.people_and_rates.sidepanel.alerts.setting_custom_rate.title')
  const alertDescription = areJobLevelRates
    ? i18n.t('project_management.jobs_and_rates.sidepanels.alerts.not_affect_projects.description')
    : areProjectWorkerRates
    ? i18n.t('project_management.project_workers.details.alerts.rate_changed.description')
    : i18n.t('project_management.people_and_rates.sidepanel.alerts.setting_custom_rate.description')

  const notReferenceBillingRateValue = referenceBillingRate !== parseInt(billRate)

  return (
    <Card>
      <Card.Content>
        <Box width="full">
          <Box flexDirection="row" gap="s8" paddingBottom="s32">
            {areProjectWorkerRates ? (
              <F0TagRaw
                icon={referenceBillingRate === billingRateCents ? Link : LinkRemove}
                text={
                  referenceBillingRate === billingRateCents
                    ? i18n.t('project_management.project_workers.details.default_rate')
                    : i18n.t('project_management.people_and_rates.sidepanel.tags.custom')
                }
              />
            ) : (
              <Text size="300" color="grey1000" weight="semibold">
                {i18n.t('project_management.jobs_and_rates.sidepanels.new_rate_button')}
              </Text>
            )}
          </Box>
          <RatesSingleForm
            billRate={billRate}
            currency={currency}
            errors={errors}
            maxCostRate={maxCostRate.toString()}
            minProfitMargin={minProfitMargin}
            handleBillRateChange={handleBillRateChange}
            handleProfitMarginChange={handleProfitMarginChange}
            areJobLevelRates={areJobLevelRates}
          />
          {!areJobLevelRates && notReferenceBillingRateValue && referenceBillingRate && (
            <Box
              onClick={() => {
                setBillRate(referenceBillingRate.toString())
                const profitMargin = calculateProfitMargin(
                  maxCostRate,
                  referenceBillingRate.toString()
                )
                setMinProfitMargin(profitMargin)
              }}
              alignSelf="center"
              paddingTop="s6"
              paddingRight="s40"
            >
              <Text underlined size="200" color="grey1000" weight="semibold">
                {areProjectWorkerRates
                  ? i18n.t('project_management.project_workers.details.reser_to_default')
                  : i18n.t('project_management.people_and_rates.form.reset_to_job_rate')}
              </Text>
            </Box>
          )}
        </Box>
        {!areProjectWorkerRates && (
          <Box paddingTop="s20" width="full">
            <DayPicker
              readonly
              label={i18n.t(
                'project_management.jobs_and_rates.sidepanels.form.efective_dates.label'
              )}
              value={moment(new Date()).format('L')}
              onChange={() => {}}
            />
          </Box>
        )}

        {notReferenceBillingRateValue && (
          <Box paddingTop="s24">
            <Alert variant={areJobLevelRates ? 'info' : 'warning'}>
              <AlertTitle>{alertTitle}</AlertTitle>
              <AlertDescription>{alertDescription}</AlertDescription>
            </Alert>
          </Box>
        )}

        <Box flexDirection="row" gap="s8" paddingBottom="s8" paddingTop="s32" alignSelf="flexEnd">
          {onClose && (
            <Button type="secondary" onClick={onClose}>
              {i18n.t('common.cancel')}
            </Button>
          )}
          <Button disabled={!hasInitialValuesChanged || !!errors.billRate} onClick={handleSubmit}>
            {i18n.t('common.save_changes')}
          </Button>
        </Box>
      </Card.Content>
    </Card>
  )
}

export default EditRateCard
